import { useCallback, useRef } from 'react';

import {
  guardarMidia,
  limparMidias,
  lerMidias,
  removerMidia,
  type OrigemDaMidia,
} from '../infrastructure/offline/rascunhoMidiasDb';
import { notify } from '../shared/feedback/notify';

export interface MidiaLocal {
  origem: OrigemDaMidia;
  grupo: string;
  file: File;
}

const novoId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const avisarFalha = () =>
  notify.warning(
    'Não foi possível guardar neste navegador as fotos ainda não enviadas. Se a página recarregar antes de salvar, será preciso anexá-las de novo.',
    { toastId: 'rascunho-midias-falha' },
  );

/**
 * Mídias pendentes do checklist no aparelho (oficina/at-136). Cada `File` em tela é ligado a um
 * registro do IndexedDB; `sincronizar` grava o que entrou e apaga o que saiu (enviado ou removido),
 * sem regravar os arquivos que já estavam guardados — lote de 100+ fotos não pode ser reescrito
 * a cada clique.
 */
export function useMidiasDoRascunho(chave: string | null) {
  const idPorArquivo = useRef(new Map<File, string>());
  const ordem = useRef(0);

  const restaurar = useCallback(async (): Promise<MidiaLocal[]> => {
    idPorArquivo.current = new Map();
    if (!chave) return [];
    try {
      const guardadas = await lerMidias(chave);
      ordem.current = guardadas.reduce((max, m) => Math.max(max, m.ordem + 1), 0);
      return guardadas.map((m) => {
        const file = new File([m.blob], m.nome, { type: m.tipo });
        idPorArquivo.current.set(file, m.id);
        return { origem: m.origem, grupo: m.grupo, file };
      });
    } catch {
      notify.warning(
        'Não foi possível ler as fotos guardadas neste navegador. Confira as mídias antes de salvar o checklist.',
        { toastId: 'rascunho-midias-falha-leitura' },
      );
      return [];
    }
  }, [chave]);

  const sincronizar = useCallback(
    async (atuais: MidiaLocal[]) => {
      if (!chave) return;
      const vistos = new Set<File>();
      try {
        for (const m of atuais) {
          vistos.add(m.file);
          if (idPorArquivo.current.has(m.file)) continue;
          const id = novoId();
          idPorArquivo.current.set(m.file, id);
          await guardarMidia({
            id,
            chave,
            origem: m.origem,
            grupo: m.grupo,
            nome: m.file.name,
            tipo: m.file.type,
            blob: m.file,
            ordem: ordem.current++,
          });
        }
        for (const [file, id] of Array.from(idPorArquivo.current.entries())) {
          if (vistos.has(file)) continue;
          idPorArquivo.current.delete(file);
          await removerMidia(id);
        }
      } catch {
        avisarFalha();
      }
    },
    [chave],
  );

  const limpar = useCallback(async () => {
    idPorArquivo.current = new Map();
    if (!chave) return;
    try {
      await limparMidias(chave);
    } catch {
      // Silêncio deliberado: limpar acontece depois de o checklist ser salvo ou descartado, e a
      // sobra só reapareceria se o MESMO checklist fosse aberto de novo neste navegador.
    }
  }, [chave]);

  return { restaurar, sincronizar, limpar };
}
